/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Zoom-control bij een cytoscape-canvas: twee zoomknoppen, een resetknop en een slider die het
 * zoomniveau logaritmisch over de sliderbreedte verdeelt. Vervangt de AngularJS-directive
 * `dlpanzoom`. Het canvas wordt niet als binding meegegeven maar via de canvasregistratie
 * opgehaald (`whenCanvas`; tot golf 4f via de migratie-interface).
 *
 * Wordt alleen vanuit `dl-canvasknoppen` gebruikt.
 *
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 22-07-2026
 */
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    signal,
    ViewChild,
    inject
} from '@angular/core';
import {TranslatePipe} from '@ngx-translate/core';
import type {Core} from 'cytoscape';
import {BeginneruitlegDirective} from '../../directives/beginnersuitleg/beginnersuitleg.directive';
import {CanvasregistratieService, type CytoCanvasView} from '../../app/canvas/canvasregistratie.service';
import {WeergaveInstellingenService} from '../../services/weergave-instellingen.service';

/** Ruimte tussen de sliderhandle en de randen van de slider. */
const SLIDER_MARGE = 2;

/** Percentage waarmee de zoomknoppen het niveau per stap verhogen of verlagen. */
const ZOOMFACTOR = 0.05;

/** Aantal milliseconden tussen twee stappen zolang een zoomknop ingedrukt blijft. */
const ZOOMHERHALING_MS = 45;

/** Padding waarmee de resetknop het model in beeld past. */
const FIT_PADDING = 44;

@Component({
    selector: 'dl-panzoom',
    standalone: true,
    templateUrl: './dl-panzoom.component.html',
    styleUrls: ['./dl-panzoom.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslatePipe, BeginneruitlegDirective],
    host: {
        '[class.dl-panzoom--gereed]': 'gereed()'
    }
})
export class DlPanzoomComponent implements OnInit, OnDestroy {

    /**
     * De canvasregistratie, voor het ophalen van de cytoscape-instantie van het canvas.
     */
    private readonly canvasregistratie = inject(CanvasregistratieService);

    /**
     * De weergave-instellingen: bij beginnersmodus vervangen de uitlegballonnen de browser-titels.
     */
    protected readonly weergaveInstellingen = inject(WeergaveInstellingenService);

    /**
     * De sliderbalk, voor het omrekenen van een muispositie naar een zoomniveau.
     */
    @ViewChild('slider', {static: true}) private slider!: ElementRef<HTMLDivElement>;

    /**
     * De sliderhandle; zijn breedte telt mee in het bruikbare bereik van de slider.
     */
    @ViewChild('handle', {static: true}) private handle!: ElementRef<HTMLDivElement>;

    // --- Inputs ---

    /** Het canvas waaraan deze control gekoppeld is. */
    @Input({required: true}) view!: CytoCanvasView;

    /** Het laagste zoomniveau dat de control instelt. */
    @Input() minZoom = 0.1;

    /** Het hoogste zoomniveau dat de control instelt. */
    @Input() maxZoom = 10;

    // --- Interne state ---

    /** Of het canvas beschikbaar is; daarvoor heeft de control niets te bedienen. */
    protected readonly gereed = signal(false);

    /** De positie van de handle in pixels vanaf de linkerrand van de slider. */
    protected readonly handleLinks = signal(SLIDER_MARGE);

    /** Of de gebruiker de handle op dit moment versleept. */
    protected readonly sleept = signal(false);

    /** De cytoscape-instantie van het gekoppelde canvas. */
    private cy: Core | null = null;

    /** De timer van een ingedrukte zoomknop. */
    private zoomherhaling: ReturnType<typeof setInterval> | null = null;

    /** De afstand tussen de aanwijzer en de linkerrand van de handle bij het begin van een sleep. */
    private handlegreep = 0;

    /** De laatst bekende aanwijzerpositie tijdens het slepen, verwerkt op de volgende frame. */
    private sleeppositie: number | null = null;

    /** Het openstaande animatieframe waarin de sleep verwerkt wordt. */
    private sleepframe = 0;

    /**
     * Herpositioneert de handle wanneer het canvas van zoomniveau verandert, bijvoorbeeld door het
     * muiswiel of een fit. Als property bewaard zodat de listener afgemeld kan worden.
     */
    private readonly bijZoom = () => this.positioneerHandle();

    // --- Lifecycle hooks ---

    /**
     * Wacht op de cytoscape-instantie van het canvas en zet de handle op het huidige zoomniveau.
     */
    async ngOnInit(): Promise<void> {
        this.cy = await this.canvasregistratie.whenCanvas(this.view);
        this.cy.on('zoom', this.bijZoom);
        this.gereed.set(true);
        this.positioneerHandle();
    }

    /**
     * Meldt de zoomlistener af en stopt lopende timers.
     */
    ngOnDestroy(): void {
        this.cy?.removeListener('zoom', this.bijZoom);
        this.stopZoomen();
        cancelAnimationFrame(this.sleepframe);
    }

    // --- Interactie ---

    /**
     * Start het in- (richting 1) of uitzoomen (richting -1) en blijft herhalen zolang de knop
     * ingedrukt is.
     */
    protected startZoomen(richting: 1 | -1): void {
        this.stopZoomen();
        this.zoomStap(richting);
        this.zoomherhaling = setInterval(() => this.zoomStap(richting), ZOOMHERHALING_MS);
    }

    /**
     * Stopt een lopende zoomherhaling. Ook gekoppeld aan het loslaten van de knop buiten de control
     * en aan het verlaten van het venster.
     */
    @HostListener('document:pointerup')
    @HostListener('document:pointercancel')
    @HostListener('window:blur')
    protected stopZoomen(): void {
        if (this.zoomherhaling) {
            clearInterval(this.zoomherhaling);
            this.zoomherhaling = null;
        }
    }

    /**
     * Past het model in beeld. Bij een leeg canvas gaat de weergave terug naar de uitgangspositie.
     */
    protected reset(): void {
        if (!this.cy) {
            return;
        }
        if (this.cy.elements().size() === 0) {
            this.cy.reset();
        } else {
            this.cy.fit(undefined, FIT_PADDING);
            this.cy.center();
        }
    }

    /**
     * Een klik naast de handle verplaatst hem naar die plek op de slider.
     */
    protected klikSlider(gebeurtenis: PointerEvent): void {
        if (this.handle.nativeElement.contains(gebeurtenis.target as Node)) {
            return;
        }
        this.zoomNaarPositie(gebeurtenis.clientX, 0);
    }

    /**
     * Begint het slepen van de handle. De aanwijzer wordt aan de handle gekoppeld, zodat het slepen
     * doorloopt buiten de slider en het loslaten altijd bij ons terechtkomt.
     */
    protected startSlepen(gebeurtenis: PointerEvent): void {
        gebeurtenis.preventDefault();
        this.stopZoomen();
        this.handlegreep = gebeurtenis.clientX - this.handle.nativeElement.getBoundingClientRect().left;
        this.sleept.set(true);
        this.handle.nativeElement.setPointerCapture(gebeurtenis.pointerId);
    }

    /**
     * Verwerkt het slepen; de zoomactie zelf wordt tot de volgende frame uitgesteld zodat een snelle
     * muisbeweging het canvas niet overspoelt.
     */
    protected sleep(gebeurtenis: PointerEvent): void {
        if (!this.sleept()) {
            return;
        }
        this.sleeppositie = gebeurtenis.clientX;
        if (!this.sleepframe) {
            this.sleepframe = requestAnimationFrame(() => {
                this.sleepframe = 0;
                if (this.sleeppositie !== null) {
                    this.zoomNaarPositie(this.sleeppositie, this.handlegreep);
                }
            });
        }
    }

    /**
     * Beëindigt het slepen van de handle.
     */
    protected stopSlepen(gebeurtenis: PointerEvent): void {
        this.sleept.set(false);
        this.sleeppositie = null;
        this.handle.nativeElement.releasePointerCapture(gebeurtenis.pointerId);
    }

    // --- Private methoden ---

    /**
     * Zet de handle op de plek die hoort bij het huidige zoomniveau van het canvas. Doet niets
     * zolang de control geen breedte heeft (verborgen tijdens een instructievideo bijvoorbeeld):
     * de maten zijn dan 0 en de handle zou naar links klappen.
     */
    private positioneerHandle(): void {
        const bereik = this.sliderbereik();
        if (!this.cy || !bereik) {
            return;
        }
        // zoom = maxZoom ^ p, met p lopend van x (negatief) tot 1
        const x = Math.log(this.minZoom) / Math.log(this.maxZoom);
        const p = Math.log(this.cy.zoom()) / Math.log(this.maxZoom);
        const percentage = (p - x) / (1 - x);
        this.handleLinks.set(this.klem(percentage * (bereik.max - bereik.min), bereik.min, bereik.max));
    }

    /**
     * Rekent een horizontale aanwijzerpositie om naar een zoomniveau en past dat toe. `greep` is de
     * afstand tussen de aanwijzer en de linkerrand van de handle, zodat die tijdens het slepen niet
     * onder de muis wegspringt.
     */
    private zoomNaarPositie(clientX: number, greep: number): void {
        const bereik = this.sliderbereik();
        if (!bereik) {
            return;
        }
        const links = this.klem(clientX - this.slider.nativeElement.getBoundingClientRect().left - greep, bereik.min, bereik.max);
        const percentage = (links - bereik.min) / (bereik.max - bereik.min);
        const x = Math.log(this.minZoom) / Math.log(this.maxZoom);
        const niveau = Math.pow(this.maxZoom, (1 - x) * percentage + x);
        this.zoomNaar(this.klem(niveau, this.minZoom, this.maxZoom));
    }

    /**
     * Zet één zoomstap: het huidige niveau maal de zoomfactor, in de opgegeven richting.
     */
    private zoomStap(richting: 1 | -1): void {
        if (!this.cy) {
            return;
        }
        const huidig = this.cy.zoom();
        const niveau = this.klem(huidig * (1 + richting * ZOOMFACTOR), this.minZoom, this.maxZoom);
        if (niveau !== huidig) {
            this.zoomNaar(niveau);
        }
    }

    /**
     * Zoomt het canvas naar het opgegeven niveau, rond het midden van het canvas: daarmee blijft
     * wat in beeld staat op zijn plek.
     */
    private zoomNaar(niveau: number): void {
        const container = this.cy?.container();
        if (container) {
            this.cy?.zoom({level: niveau, renderedPosition: {x: container.clientWidth / 2, y: container.clientHeight / 2}});
        }
    }

    /**
     * De bruikbare linkerposities van de handle binnen de slider, of null als de control geen
     * afmetingen heeft.
     */
    private sliderbereik(): {min: number; max: number} | null {
        const breedte = this.slider.nativeElement.clientWidth;
        return breedte ? {min: SLIDER_MARGE, max: breedte - this.handle.nativeElement.clientWidth - 2 * SLIDER_MARGE} : null;
    }

    /**
     * Begrenst een waarde tussen een onder- en bovengrens.
     */
    private klem(waarde: number, min: number, max: number): number {
        return Math.min(Math.max(waarde, min), max);
    }
}
